// client/src/lib/groupSummaryPdf.js
// Downloads a Group Summary PDF generated by the backend.

import { apiFetch } from './api';
import { DateTime } from 'luxon';

function safeFilenamePart(s) {
  return String(s || '')
    .trim()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

function filenameFromContentDisposition(header) {
  if (!header) return null;
  // e.g. attachment; filename="Group_19_My_Group.pdf"
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return m ? m[1] : null;
}

async function fetchPdf(groupId) {
  // Preferred
  let res = await apiFetch(`/api/groups/${groupId}/pdf`, {
    method: 'GET',
    headers: { Accept: 'application/pdf' },
  });

  // Back-compat fallback
  if (res.status === 404) {
    res = await apiFetch(`/api/groups/${groupId}/summary-pdf`, {
      method: 'GET',
      headers: { Accept: 'application/pdf' },
    });
  }

  return res;
}

/**
 * Download the Group Summary PDF (organiser-only).
 *
 * This calls: GET /api/groups/:groupId/pdf
 */
export async function downloadGroupSummaryPdf(groupId, groupName = '', groupTimezone = null) {
  if (!groupId) throw new Error('Missing groupId');

  const res = await fetchPdf(groupId);

  if (!res.ok) {
    // Try JSON first
    let msg = `Failed to generate PDF (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) msg = data.error;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);

  const cd = res.headers?.get?.('content-disposition');
  const serverName = filenameFromContentDisposition(cd);

  const base = safeFilenamePart(groupName) || `group_${groupId}`;

  // If the server filename can't be read (eg header not exposed by CORS in some envs),
  // append a timestamp in the group's timezone so the download matches expectations.
  // Format: 202601 2006
  const tz = groupTimezone;
  let stampDt = DateTime.local();
  if (tz) stampDt = stampDt.setZone(tz);
  if (!stampDt.isValid) stampDt = DateTime.utc();
  const stamp = stampDt.toFormat('yyyyLL HHmm');

  const fallbackName = `Group_${groupId}_${base}_${stamp}.pdf`;
  const filename = serverName || fallbackName;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  // cleanup
  setTimeout(() => {
    try {
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }, 3000);
}

// Back-compat aliases (in case a page imports a different name)
export const generateGroupSummaryPdf = async (payload) => {
  const gid = payload?.group?.id;
  return downloadGroupSummaryPdf(gid, payload?.group?.name);
};

export default downloadGroupSummaryPdf;
